import { useNavigate } from 'react-router-dom'
import { AlertTriangle, ChevronRight, Landmark, ShieldCheck, type LucideIcon } from 'lucide-react'
import { useFinancialConnections, useTransactions } from '@/hooks'
import { cn } from '@/lib/utils'

interface MoneyWatchCardProps {
  familyId: string | null
}

interface MoneyWatchState {
  icon: LucideIcon
  iconClassName: string
  title: string
  subtitle: string
}

/**
 * The single Money watch row on the caregiver home. Reassuring by default
 * ("Finances look good"), a warning chip when transactions are flagged, and
 * a terracotta connect prompt when no bank is linked yet.
 */
export function MoneyWatchCard({ familyId }: MoneyWatchCardProps) {
  const navigate = useNavigate()

  const { connections, isLoading: isLoadingConnections } = useFinancialConnections(familyId)
  const { pagination: flaggedPagination } = useTransactions(familyId, {
    flagged: true,
    page_size: 1,
  })
  const { pagination: watchedPagination } = useTransactions(familyId, { page_size: 1 })

  const hasConnection = connections.length > 0
  const flaggedCount = flaggedPagination?.total_count ?? 0
  const watchedCount = watchedPagination?.total_count ?? 0

  let state: MoneyWatchState
  if (!hasConnection && !isLoadingConnections) {
    state = {
      icon: Landmark,
      iconClassName: 'bg-accent-600/10 text-accent-600',
      title: 'Set up money watch',
      subtitle: 'Connect a bank account to watch for unusual charges',
    }
  } else if (flaggedCount > 0) {
    state = {
      icon: AlertTriangle,
      iconClassName: 'bg-warning-600/10 text-warning-600',
      title:
        flaggedCount === 1
          ? '1 transaction worth a look'
          : `${flaggedCount} transactions worth a look`,
      subtitle: "Tap to review what we've noticed",
    }
  } else {
    state = {
      icon: ShieldCheck,
      iconClassName: 'bg-success-600/10 text-success-600',
      title: 'Finances look good',
      subtitle:
        watchedCount > 0
          ? `${watchedCount.toLocaleString()} transactions watched`
          : "We're watching for unusual activity",
    }
  }

  const Icon = state.icon

  return (
    <button
      type="button"
      onClick={() => navigate('/financial')}
      className="hearth-card hearth-press flex w-full items-center gap-3 p-4 text-left"
    >
      <span
        className={cn(
          'flex h-10 w-10 shrink-0 items-center justify-center rounded-full',
          state.iconClassName
        )}
      >
        <Icon className="h-5 w-5" />
      </span>
      <span className="min-w-0 flex-1">
        <span className="block truncate font-medium text-gray-900">{state.title}</span>
        <span className="mt-0.5 block text-[13px] text-gray-500">{state.subtitle}</span>
      </span>
      <ChevronRight className="h-5 w-5 shrink-0 text-gray-400" />
    </button>
  )
}
